import { z } from 'zod';

export type UsdMinor = bigint & { readonly __brand: 'UsdMinor' };
export type MicroUsd = bigint & { readonly __brand: 'MicroUsd' };
export const moneyMinorStringSchema = z.string().regex(/^\d+$/u);
export const nullableMoneyMinorStringSchema = moneyMinorStringSchema.nullable();
export const positiveMoneyMinorStringSchema = z
  .string()
  .regex(/^[1-9]\d{0,17}$/u);

export function moneyMinorDecimal(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? '-' : ''}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

export const usdMinor = (value: bigint): UsdMinor => value as UsdMinor;
export const microUsd = (value: bigint): MicroUsd => value as MicroUsd;

export const ceilDivide = (numerator: bigint, denominator: bigint): bigint => {
  if (numerator < 0n || denominator <= 0n) {
    throw new Error(
      'ceilDivide requires a non-negative numerator and positive denominator.'
    );
  }
  return (numerator + denominator - 1n) / denominator;
};

export const tokenCostMicroUsd = (
  tokens: bigint,
  priceMicroUsdPerMillionTokens: bigint
): MicroUsd => {
  if (tokens < 0n || priceMicroUsdPerMillionTokens < 0n) {
    throw new Error('Token quantities and prices cannot be negative.');
  }
  return microUsd(
    ceilDivide(tokens * priceMicroUsdPerMillionTokens, 1_000_000n)
  );
};

export const addMicroUsd = (values: readonly MicroUsd[]): MicroUsd =>
  microUsd(values.reduce((total, value) => total + value, 0n));

export function formatExactMoney(
  value: bigint,
  currency: 'PEN' | 'USD',
  scale: 2 | 6,
  locale = 'es-PE'
): string {
  const divisor = 10n ** BigInt(scale);
  const absolute = value < 0n ? -value : value;
  const integer = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(absolute / divisor);
  const decimal = new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((part) => part.type === 'decimal')?.value;
  if (!decimal) throw new Error('Locale decimal separator unavailable.');
  const fraction = (absolute % divisor).toString().padStart(scale, '0');
  return `${currency} ${value < 0n ? '-' : ''}${integer}${decimal}${fraction}`;
}

export function toMoneyMinor(value: string, multiplier = 1n): bigint | null {
  const normalized = value.replace(/\s/gu, '');
  const decimal = /[.,](\d{1,2})$/u.exec(normalized);
  const whole = (
    decimal ? normalized.slice(0, decimal.index) : normalized
  ).replace(/[.,]/gu, '');
  if (!/^\d+$/u.test(whole)) return null;
  return (
    (BigInt(whole) * 100n +
      BigInt((decimal?.[1] ?? '').padEnd(2, '0') || '0')) *
    multiplier
  );
}
